import * as XLSX from "xlsx";

export type TableColumn = {
  key: string;          
  label: string;           
  dataIndex?: string;    
};


type ExportOptions = {
  fileName?: string;      
  sheetName?: string;
  flatten?: boolean;
};

const getByPath = (obj: any, path?: string) => {
  if (!path) return undefined;
  return path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj);
};


const flattenObject = (obj: any, prefix = ""): Record<string, any> => {
  if (obj == null || typeof obj !== "object") return { [prefix.slice(1)]: obj };
  const out: Record<string, any> = {};
  for (const [k, v] of Object.entries(obj)) {
    const key = `${prefix}.${k}`;
    if (v && typeof v === "object" && !Array.isArray(v) && !(v instanceof Date)) {
      Object.assign(out, flattenObject(v, key));
    } else {
      out[key.slice(1)] = v;
    }
  }
  //  
  return out;
};

export function exportRowsToXlsx(
  rows: any[],
  columns: TableColumn[],
  opts: ExportOptions = {}
) {
  const { fileName = "export", sheetName = "Data", flatten = false } = opts;
  const columnsToUse = columns.filter((c => c.key!=="actions" && c.label!=="Actions"));
   
   
  const out = rows.map((row) => {
    const base =  flattenObject(row);
    const o: Record<string, any> = {};
    for (const col of columnsToUse) {
      const header = col.label || col.key || col.dataIndex || "";
      const value =
        col.dataIndex
          ? getByPath(base, col.dataIndex)
          : base[col.key] ?? base[col.label as keyof typeof base];

      let cell = value;
      if (cell instanceof Date) cell = cell.toISOString();
      if (Array.isArray(cell)) cell = cell.join(", ");
      if (cell == null) cell = ""; 

      o[header] = cell;
    }
    return o;
  });

  const ws = XLSX.utils.json_to_sheet(out, { skipHeader: false });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);

  const colWidths = columns.map((c, i) => {
    const header = c.label || c.key || c.dataIndex || "";
    const maxCellLen = Math.max(
      header.length,
      ...out.map((r) => String(r[header] ?? "").length)
    );
    return { wch: Math.min(Math.max(maxCellLen + 2, 8), 60) };
  });
  (ws["!cols"] as any) = colWidths;

  const d = new Date();
  const ts = d.toISOString().slice(0, 10); 
  
  const finalName = `${fileName}_${ts}.xlsx`;

  XLSX.writeFile(wb, finalName);
}


export function exportRowsToCsv(
  rows: any[],
  columns: TableColumn[],
  opts: { fileName?: string } = {}
) {
  const { fileName = "export" } = opts;
  const headers = columns.map((c) => c.label || c.key || c.dataIndex || "");
  const csvRows = [
    headers.join(","),
    ...rows.map((row) =>
      columns
        .map((c) => {
          const v = c.dataIndex ? getByPath(row, c.dataIndex) : row[c.key];
          const text = v == null ? "" : String(Array.isArray(v) ? v.join("; ") : v);
          const needsQuotes = /[",\n]/.test(text);
          return needsQuotes ? `"${text.replace(/"/g, '""')}"` : text;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvRows], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
